import React, { useRef, useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import {
  wrapTextRange,
  prefixLines,
  NOTE_SIZE_OPTIONS,
} from '../utils/studentNotesMarkdown';

function applyFormat(text, selection, op) {
  const { start, end } = selection;
  switch (op) {
    case 'bold':
      return wrapTextRange(text, start, end, '**', '**');
    case 'italic':
      return wrapTextRange(text, start, end, '*', '*');
    case 'underline':
      return wrapTextRange(text, start, end, '<u>', '</u>');
    case 'bullet':
      return prefixLines(text, start, end, '- ');
    case 'indent':
      return prefixLines(text, start, end, '    ');
    case 'outdent': {
      const s = Math.max(0, Math.min(start, text.length));
      const e = Math.max(s, Math.min(end, text.length));
      const block = text.slice(s, e);
      const lines = block.split(/\r?\n/).map((ln) => ln.replace(/^(\s{1,4}|- )/, ''));
      const next = `${text.slice(0, s)}${lines.join('\n')}${text.slice(e)}`;
      return { text: next, selectionStart: s, selectionEnd: s + lines.join('\n').length };
    }
    case 'large':
      return prefixLines(text, start, end, '# ');
    case 'medium':
      return prefixLines(text, start, end, '## ');
    case 'small': {
      const s = Math.max(0, Math.min(start, text.length));
      const e = Math.max(s, Math.min(end, text.length));
      const block = text.slice(s, e);
      const lines = block.split(/\r?\n/).map((ln) => ln.replace(/^#{1,2}\s+/, ''));
      const next = `${text.slice(0, s)}${lines.join('\n')}${text.slice(e)}`;
      return { text: next, selectionStart: s, selectionEnd: s + lines.join('\n').length };
    }
    default:
      return { text, selectionStart: start, selectionEnd: end };
  }
}

export default function StudentNotesEditor({ body, onBodyChange, disabled }) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const inputRef = useRef(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [dictating, setDictating] = useState(false);
  const recognitionRef = useRef(null);

  const runFormat = useCallback(
    (op) => {
      const result = applyFormat(body, selection, op);
      onBodyChange(result.text);
      setSelection({ start: result.selectionStart, end: result.selectionEnd });
      if (Platform.OS === 'web' && inputRef.current) {
        setTimeout(() => {
          try {
            const el = inputRef.current;
            if (el?.setSelectionRange) {
              el.setSelectionRange(result.selectionStart, result.selectionEnd);
            }
          } catch (_) {
            /* ignore */
          }
        }, 0);
      }
    },
    [body, selection, onBodyChange]
  );

  const toggleDictation = useCallback(() => {
    if (Platform.OS !== 'web') {
      Alert.alert(
        'Voice dictation',
        'Voice dictation is available on web. Type your note on this device, or open the app in a browser.'
      );
      return;
    }
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) {
      Alert.alert('Not supported', 'Speech recognition is not available in this browser.');
      return;
    }
    if (dictating && recognitionRef.current) {
      recognitionRef.current.stop();
      setDictating(false);
      return;
    }
    const rec = new Ctor();
    recognitionRef.current = rec;
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = 'en-GB';
    rec.onresult = (event) => {
      let chunk = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i].isFinal) {
          chunk += event.results[i][0].transcript;
        }
      }
      if (chunk) {
        const base = String(body || '');
        const sep = base.length && !base.endsWith('\n') && !base.endsWith(' ') ? ' ' : '';
        onBodyChange(`${base}${sep}${chunk}`);
      }
    };
    rec.onerror = () => setDictating(false);
    rec.onend = () => setDictating(false);
    rec.start();
    setDictating(true);
  }, [dictating, body, onBodyChange]);

  const toolbarGroups = [
    [
      { key: 'bold', label: 'B', style: styles.toolBold, op: 'bold' },
      { key: 'italic', label: 'I', style: styles.toolItalic, op: 'italic' },
      { key: 'underline', label: 'U', style: styles.toolUnderline, op: 'underline' },
    ],
    [
      { key: 'bullet', label: '•', op: 'bullet' },
      { key: 'indent', label: '→', op: 'indent' },
      { key: 'outdent', label: '←', op: 'outdent' },
    ],
  ];

  return (
    <View style={styles.wrap} nativeID="student-notes-editor-wrap">
      <View style={styles.toolbar} nativeID="student-notes-toolbar">
        {toolbarGroups.map((group, gi) => (
          <View key={`g-${gi}`} style={styles.toolGroup}>
            {group.map((btn) => (
              <TouchableOpacity
                key={btn.key}
                style={[styles.toolBtn, dictating && btn.key === 'mic' && styles.toolBtnActive]}
                onPress={() => runFormat(btn.op)}
                disabled={disabled}
                activeOpacity={0.75}
              >
                <Text style={[styles.toolBtnText, btn.style]}>{btn.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        ))}
        <View style={styles.toolGroup}>
          {NOTE_SIZE_OPTIONS.map((sz) => (
            <TouchableOpacity
              key={sz.id}
              style={styles.sizeBtn}
              onPress={() => runFormat(sz.id)}
              disabled={disabled}
              activeOpacity={0.75}
            >
              <Text style={styles.sizeBtnText}>{sz.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity
          style={[styles.micBtn, dictating && styles.toolBtnActive]}
          onPress={toggleDictation}
          disabled={disabled}
          activeOpacity={0.75}
        >
          <Text style={styles.micBtnText}>{dictating ? '■ Stop' : '🎤 Voice'}</Text>
        </TouchableOpacity>
      </View>
      <TextInput
        ref={inputRef}
        nativeID="student-notes-body-input"
        style={styles.bodyInput}
        multiline
        value={body}
        onChangeText={onBodyChange}
        onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
        placeholder="Write your training notes…"
        placeholderTextColor={theme.isDarkMode ? '#777' : '#999'}
        textAlignVertical="top"
        editable={!disabled}
        scrollEnabled
        {...(Platform.OS === 'web'
          ? { dataSet: { studentNotesBodyInput: 'true' } }
          : {})}
      />
      <Text style={styles.hint}>
        Formatting uses markdown-style markers (bold, headings, bullets). Voice dictation adds text at
        the end on web.
      </Text>
    </View>
  );
}

const NOTES_CREAM = '#faf8f4';
const NOTES_CREAM_BORDER = '#ebe4d6';

function getStyles(theme) {
  const gold = theme.colors.primary || '#d4af37';
  const border = theme.isDarkMode
    ? theme.colors.border || '#444'
    : NOTES_CREAM_BORDER;
  return StyleSheet.create({
    wrap: {
      flex: 1,
      minHeight: 120,
      width: '100%',
      maxWidth: '100%',
      alignSelf: 'stretch',
    },
    toolbar: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 8,
      paddingHorizontal: 4,
      borderBottomWidth: 1,
      borderBottomColor: border,
      marginBottom: 8,
    },
    toolGroup: {
      flexDirection: 'row',
      alignItems: 'center',
      marginRight: 4,
      paddingRight: 6,
      borderRightWidth: 1,
      borderRightColor: border,
    },
    toolBtn: {
      minWidth: 32,
      height: 32,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#f0f0f0',
      justifyContent: 'center',
      alignItems: 'center',
      marginHorizontal: 2,
      paddingHorizontal: 6,
    },
    toolBtnActive: {
      backgroundColor: gold,
    },
    toolBtnText: { fontSize: 15, color: theme.colors.text, fontWeight: '600' },
    toolBold: { fontWeight: '800' },
    toolItalic: { fontStyle: 'italic' },
    toolUnderline: { textDecorationLine: 'underline' },
    sizeBtn: {
      paddingHorizontal: 8,
      height: 32,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#f0f0f0',
      justifyContent: 'center',
      marginHorizontal: 2,
    },
    sizeBtnText: { fontSize: 12, color: theme.colors.text, fontWeight: '600' },
    micBtn: {
      paddingHorizontal: 10,
      height: 32,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#f0f0f0',
      justifyContent: 'center',
      marginLeft: 4,
    },
    micBtnText: { fontSize: 12, color: theme.colors.text, fontWeight: '600' },
    bodyInput: {
      flex: 1,
      minHeight: 200,
      fontSize: 16,
      lineHeight: 24,
      color: theme.colors.text,
      backgroundColor: theme.isDarkMode ? '#121212' : NOTES_CREAM,
      borderWidth: 1,
      borderColor: border,
      borderRadius: 8,
      padding: 14,
      ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    hint: {
      fontSize: 11,
      color: theme.isDarkMode ? '#888' : '#666',
      marginTop: 8,
      fontStyle: 'italic',
    },
  });
}
